import { CivilStatus } from '@idc-forms/types/civil-status'
import { Gender } from '@idc-forms/types/gender'
import { z } from 'zod'
import { requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Representante legal de una sociedad — fuente única de verdad para la
 * validación del API (nestjs-zod `createZodDto`) y del formulario web
 * (RHF + zodResolver).
 *
 * Regla de dominio: cada sociedad tiene A LO SUMO un representante
 * (`societyId` es único en la colección — el service lo pre-chequea con
 * `ERROR_REPRESENTATIVE_SOCIETY_ALREADY_HAS_ONE`).
 *
 * Los textos legales van NORMALIZADOS a MAYÚSCULAS en el schema (alimentan
 * los PDFs de FEIC/promesa — el legacy lo hacía en transforms del form).
 */

// ObjectId de Mongo serializado (24 hex): el wire actual identifica la
// sociedad por ObjectId; no cambiar hasta contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

/** Texto legal: requerido, trim + MAYÚSCULAS (formato de los documentos). */
const legalText = () =>
  requiredString()
    .transform((value) => value.trim().toUpperCase())
    .pipe(requiredString())

/** Fecha plana `YYYY-MM-DD` (así se almacena y así la emite el input date). */
const plainDate = () =>
  z
    .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida')

const AddressInputSchema = z.object({
  address: legalText(),
  /** Zona (solo aplica en GT); el legacy la mandaba con default 0. */
  zone: z.number().optional(),
  /** Código ISO del país (catálogo compartido). */
  country: requiredString(),
  // El wire histórico mezcla número (form viejo) y string (Mongo): se
  // normaliza a string, que es lo que declara el schema de Mongoose.
  department: z.preprocess(
    (value) => (value === null || value === undefined ? value : String(value)),
    z.string().nullish()
  ),
  municipality: z.string().nullish()
})

const NotaryInputSchema = z.object({
  name: legalText(),
  gender: z.nativeEnum(Gender, {
    errorMap: () => ({ message: 'Debe seleccionar un género' })
  })
})

const AppointmentMercantileRegisterInputSchema = z.object({
  date: plainDate(),
  number: legalText(),
  sheet: legalText(),
  book: legalText()
})

const RepresentativeInputBaseSchema = z.object({
  name: legalText(),
  identification: requiredString(),
  gender: z.nativeEnum(Gender, {
    errorMap: () => ({ message: 'Debe seleccionar un género' })
  }),
  civilStatus: z.nativeEnum(CivilStatus, {
    errorMap: () => ({ message: 'Debe seleccionar un estado civil' })
  }),
  position: legalText(),
  profession: legalText(),
  birthdate: plainDate(),
  /** Código ISO del país de nacionalidad (catálogo compartido). */
  nationality: requiredString(),
  address: AddressInputSchema,
  notary: NotaryInputSchema,
  appointmentMercantileRegister: AppointmentMercantileRegisterInputSchema,
  societyId: objectIdString,
  active: z.boolean().optional()
})

export const CreateRepresentativeInputSchema = RepresentativeInputBaseSchema

// Parcial a nivel raíz: los subdocumentos, si vienen, vienen COMPLETOS
// (semántica del $set actual — no hay merge profundo).
export const UpdateRepresentativeInputSchema = RepresentativeInputBaseSchema.partial()

export type CreateRepresentativeInput = z.infer<typeof CreateRepresentativeInputSchema>
export type UpdateRepresentativeInput = z.infer<typeof UpdateRepresentativeInputSchema>
